/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useState } from "react";
import PropTypes from "prop-types";

import { WIDGETS, makeCustomId, widgetById } from "../../Homepage/customize/widgetRegistry";
import { CreatePanel } from "../../Homepage/customize/CustomizeDrawer";
import ComponentEditor from "./ComponentEditor";
import "../../Homepage/customize/customize.css";

/**
 * The Analytics builder's add drawer — the SAME docked drawer the workspace
 * dashboard already has (gmbw-panel / gmbw-lib-item, customize.css), carrying
 * the same component library with the same thumbnails. Nothing new to learn:
 * a row per component, + adds it to this dashboard.
 *
 * Personal cards (Greeting, My Widgets) are left out; Build-your-own is included — an
 * Analytics dashboard can be shared and put on a TV, and those read one
 * signed-in person's numbers.
 */

const COMPONENT_IDS = ["beatyeti", "yeti", "leaderboard", "podium", "kotm", "earnings", "milestones", "annualcomp", "labor", "profit", "netbonus", "deptmetrics", "goalProgress"];

// Grid translation: the registry sizes are 24-col half-units (rowHeight 8);
// the Analytics grid is 12-col (rowHeight 40). w halves; h maps ~5:1 with a
// floor so nothing lands unreadably small.
export const sizeFor = (def) => ({
  w: Math.max(4, Math.min(12, Math.round((def.defaultW || 12) / 2))),
  h: Math.max(5, Math.round(((def.defaultH || 10) * 8 + ((def.defaultH || 10) - 1) * 32) / 56)),
});

/** Minimum Analytics-grid height for a registry component (same formula as sizeFor). */
export const minSizeFor = (def) => ({
  w: Math.max(4, Math.min(12, Math.round((def.minW || def.defaultW || 12) / 2))),
  h: Math.max(5, Math.round(((def.minH || def.defaultH || 10) * 8 + ((def.minH || def.defaultH || 10) - 1) * 32) / 56)),
});

const IcPlus = "M12 5v14M5 12h14";
const IcX = "M18 6 6 18M6 6l12 12";
const Icon = ({ d }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
Icon.propTypes = { d: PropTypes.string.isRequired };

const ThumbCharts = () => (
  <svg viewBox="0 0 120 76" style={{ width: "100%", height: "100%", display: "block", background: "#fff" }} aria-hidden="true">
    <rect x="12" y="40" width="14" height="24" rx="3" fill="#C6D4E6" />
    <rect x="32" y="28" width="14" height="36" rx="3" fill="#9FB8D8" />
    <rect x="52" y="18" width="14" height="46" rx="3" fill="#5B8DEF" />
    <path d="M76 54 C84 44 90 50 96 36 C102 24 106 28 110 18" fill="none" stroke="#299E47" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export default function ComponentsDrawer({ open, onClose, onAdd, onPickMetrics, editWidget, onEditChange, onEditBack, metricCatalog = [], boardWidgets = [] }) {
  const [creating, setCreating] = useState(false);
  if (!open) return null;

  const defs = COMPONENT_IDS.map((id) => WIDGETS.find((w) => w.id === id)).filter(Boolean);

  // A placed component being edited takes over the panel — same in-drawer
  // editor flow as the workspace dashboard.
  if (editWidget) {
    return (
      <div className="gmbw-panel" role="dialog" aria-label="Edit component">
        <ComponentEditor
          widget={editWidget}
          onChange={onEditChange}
          onBack={onEditBack}
          onClose={onClose}
          metricCatalog={metricCatalog}
          boardWidgets={boardWidgets}
        />
      </div>
    );
  }

  if (creating) {
    return (
      <div className="gmbw-panel" role="dialog" aria-label="Create a component">
        <CreatePanel
          metrics={metricCatalog}
          onCancel={() => setCreating(false)}
          onClose={onClose}
          onConfirm={(settings) => {
            const id = makeCustomId();
            const def = widgetById(id) || WIDGETS.find((w) => w.id === "custom");
            const { title: customTitle, ...rest } = settings || {};
            onAdd({
              comp: id,
              label: customTitle || "Custom component",
              title: customTitle || "",
              size: sizeFor(def || { defaultW: 12, defaultH: 10 }),
              options: { ...rest },
            });
            setCreating(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="gmbw-panel" role="dialog" aria-label="Add to dashboard">
      <div className="gmbw-panel-h">
        <div className="gmbw-panel-htxt">
          <span className="gmbw-panel-title">Add to dashboard</span>
          <span className="gmbw-panel-sub">The same components as your workspace dashboard</span>
        </div>
        <button type="button" className="gmbw-panel-x" onClick={onClose} aria-label="Close">
          <Icon d={IcX} />
        </button>
      </div>

      <div className="gmbw-lib-list">
        {defs.map((def) => {
          const Thumb = def.thumb;
          return (
            <div
              key={def.id}
              className="gmbw-lib-item"
              role="button"
              tabIndex={0}
              onClick={() => onAdd({ comp: def.id, label: def.title, size: sizeFor(def) })}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onAdd({ comp: def.id, label: def.title, size: sizeFor(def) });
                }
              }}
            >
              <span className="gmbw-lib-pv" aria-hidden="true">
                <span className="gmbw-lib-frame">{Thumb ? <Thumb /> : null}</span>
              </span>
              <span className="gmbw-lib-row">
                <span className="gmbw-lib-txt">
                  <span className="gmbw-lib-name">{def.title}</span>
                  <span className="gmbw-lib-desc">{def.blurb}</span>
                </span>
                <span className="gmbw-lib-acts">
                  <button
                    type="button"
                    className="primary"
                    title="Add to this dashboard"
                    aria-label={`Add ${def.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAdd({ comp: def.id, label: def.title, size: sizeFor(def) });
                    }}
                  >
                    <Icon d={IcPlus} />
                  </button>
                </span>
              </span>
            </div>
          );
        })}

        {/* Charts keep their own picker — one row opens it. */}
        
        <div
          className="gmbw-lib-item"
          role="button"
          tabIndex={0}
          onClick={() => setCreating(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setCreating(true);
            }
          }}
        >
          <span className="gmbw-lib-pv" aria-hidden="true">
            <span className="gmbw-lib-frame">
              {(() => {
                const Thumb = (WIDGETS.find((w) => w.id === "custom") || {}).thumb;
                return Thumb ? <Thumb /> : null;
              })()}
            </span>
          </span>
          <span className="gmbw-lib-row">
            <span className="gmbw-lib-txt">
              <span className="gmbw-lib-name">Build your own</span>
              <span className="gmbw-lib-desc">Pick metrics and how they&apos;re drawn — same as Home</span>
            </span>
            <span className="gmbw-lib-acts">
              <button
                type="button"
                className="primary"
                title="Create a component"
                aria-label="Build your own component"
                onClick={(e) => {
                  e.stopPropagation();
                  setCreating(true);
                }}
              >
                <Icon d={IcPlus} />
              </button>
            </span>
          </span>
        </div>

<div
          className="gmbw-lib-item"
          role="button"
          tabIndex={0}
          onClick={onPickMetrics}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onPickMetrics();
            }
          }}
        >
          <span className="gmbw-lib-pv" aria-hidden="true">
            <span className="gmbw-lib-frame"><ThumbCharts /></span>
          </span>
          <span className="gmbw-lib-row">
            <span className="gmbw-lib-txt">
              <span className="gmbw-lib-name">Metric charts</span>
              <span className="gmbw-lib-desc">Pick metrics and chart them — number, line, bars, table…</span>
            </span>
            <span className="gmbw-lib-acts">
              <button
                type="button"
                className="primary"
                title="Pick metrics"
                aria-label="Pick metrics to chart"
                onClick={(e) => {
                  e.stopPropagation();
                  onPickMetrics();
                }}
              >
                <Icon d={IcPlus} />
              </button>
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

ComponentsDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onPickMetrics: PropTypes.func.isRequired,
  editWidget: PropTypes.object,
  onEditChange: PropTypes.func,
  onEditBack: PropTypes.func,
  metricCatalog: PropTypes.array,
  /* Every component already on this board — Revenue, Expenses & Labor's Setup
     lists the Payroll vs Revenue cards here so Labor can be pointed at one. */
  boardWidgets: PropTypes.array,
};
